import { useRef, useState } from 'react';
import IconButton from '../atoms/IconButton';
import TextArea, { type TextAreaRef } from '../atoms/TextArea';
import InputContainer from './InputContainer';
import InputContent from './InputContent';
import InputAction from './InputAction';

interface ChatInputProps {
  onSendMessage: (message: string) => Promise<void>;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const ArrowUpIcon = () => (
  <svg viewBox="0 0 26 26" className="w-full h-full">
    <path 
      d="M13 4.42L13 21.58M6.42 11L13 4.42L19.58 11" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

export function ChatInput({ onSendMessage, isLoading = false, disabled = false, placeholder = "Input" }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<TextAreaRef>(null);

  const handleSubmit = async () => {
    if (!message.trim() || isLoading || disabled) {return;}
    
    const messageToSend = message.trim();
    setMessage('');
    
    // Reset textarea height
    textareaRef.current?.reset();
    
    await onSendMessage(messageToSend);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const hasText = message.trim().length > 0;
  const containerVariant = isFocused ? 'focused' : 'default';

  return (
    <InputContainer variant={containerVariant} size="medium">
      {/* Input Content */}
      <InputContent>
        <TextArea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          autoResize
          maxHeight={300}
          // minHeight={24.7}
        />
      </InputContent>

      {/* Action Area */}
      <InputAction>
        <IconButton
          variant="key"
          size="medium"
          icon={
            isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <ArrowUpIcon />
            )
          }
          aria-label={isLoading ? "Sending..." : "Send message"}
          onClick={() => void handleSubmit()}
          state={(!hasText || isLoading || disabled) ? 'disabled' : 'enabled'}
          className="backdrop-blur-md"
        />
      </InputAction>
    </InputContainer>
  );
}